import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Buildings, Trophy, CaretDown, CaretUp } from '@phosphor-icons/react';

const About = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const infoItems = [
    {
      icon: MapPin,
      label: 'Ubicación',
      value: 'C/ Adriano 32-5º, Sevilla 41001',
      extra: 'También por videoconferencia'
    },
    {
      icon: Calendar,
      label: 'En ejercicio desde',
      value: '1994 — 30+ años'
    },
    {
      icon: Buildings,
      label: 'Despacho',
      value: 'Vilaplana Abogados'
    },
    {
      icon: Trophy,
      label: 'Casos gestionados',
      value: '+2.000 procedimientos'
    }
  ];

  const accordionItems = [
    {
      id: 'vision',
      title: 'Visión',
      content: 'Durante más de 25 años he visto el despacho como una empresa de servicios donde la innovación y la tecnología son fundamentales. Hoy lidero la transformación digital del sector jurídico, integrando inteligencia artificial y ciberseguridad para ofrecer soluciones legales del siglo XXI.'
    },
    {
      id: 'expertise',
      title: 'Especialización',
      content: 'Derecho Digital y Tecnológico • Inteligencia Artificial aplicada al Derecho • Ciberseguridad y Ciberdelincuencia • Compliance y Protección de Datos • Derecho Laboral y Civil • Negociación y Resolución de Conflictos'
    },
    {
      id: 'innovation',
      title: 'Innovación',
      content: 'Fundador de Jurisciberia SL, empresa especializada en ciberseguridad, IA y ciberdelincuencia. Experto en el uso de LLMs (ChatGPT, Claude, Gemini, Copilot) aplicados al sector jurídico. Diseñador y desarrollador de múltiples plataformas digitales especializadas en derecho y tecnología.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <section id="about" className="py-12 bg-[#050505] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-12">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-4">
              [02] Sobre mí
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Abogado visionario con tres décadas de experiencia
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: Info Cards */}
            <motion.div variants={itemVariants} className="space-y-4">
              {infoItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-white/10 card-hover"
                  data-testid={`about-info-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-blue-500/30 bg-blue-500/10">
                    <item.icon size={20} className="text-blue-500" weight="light" />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-xs text-white/50 uppercase tracking-wider mb-1">
                      {item.label}
                    </p>
                    <p className="text-white font-medium">{item.value}</p>
                    {item.extra && (
                      <a 
                        href="https://calendar.app.google/8cNXBV981iJPzp4W9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-500 hover:text-blue-400 transition-colors mt-1 inline-block"
                      >
                        {item.extra} →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Right: Accordion */}
            <motion.div variants={itemVariants} className="space-y-2">
              {accordionItems.map((item, index) => (
                <div
                  key={item.id}
                  className="accordion-item"
                  data-testid={`about-accordion-${item.id}`}
                >
                  <button
                    onClick={() => toggleSection(item.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-blue-500">
                        [0{index + 1}]
                      </span>
                      <span className="text-white font-medium">{item.title}</span>
                    </div>
                    {expandedSection === item.id ? (
                      <CaretUp size={20} className="text-white/50" />
                    ) : (
                      <CaretDown size={20} className="text-white/50" />
                    )}
                  </button>
                  <AnimatePresence>
                    {expandedSection === item.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pl-14 text-white/65 leading-relaxed">
                          {item.content}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
